import React, { FC, useContext, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { MaterialOption, MaterialSelect } from "@natera/material/lib/select";

import {
  IntlContext,
  Language,
  UppAuthContext,
  UserContext,
} from "@app/provider";
import { SecurityContext } from "@natera/platform/lib/provider/security";
import { routes } from "@app/routing";
import { useClickOutside } from "@app/hooks";
import DownArrow from "@assets/svg/icons/down-arrow.svg";

import { BillingMenuContext } from "../navigation/billingMenuContext";
import "./languageSelector.scss";

const messages = defineMessages({
  languageSelectorMenuAriaLabel: {
    id: "languageSelectorMenuAriaLabel",
    defaultMessage: "Select language",
  },
  languageSelectorMenuItemEnglishAriaLabel: {
    id: "languageSelectorMenuItemEnglishAriaLabel",
    defaultMessage: "English",
  },
  languageSelectorMenuItemSpanishAriaLabel: {
    id: "languageSelectorMenuItemSpanishAriaLabel",
    defaultMessage: "Spanish",
  },
});

const LanguageSelector: FC = () => {
  const intl = useIntl();
  const location = useLocation();

  const { isAuthorized } = useContext(SecurityContext);
  const { updateUser, updateUserLanguageNonAuth } = useContext(UserContext);
  const { profile } = useContext(UppAuthContext);
  const { changeLanguage, currentLanguage } = useContext(IntlContext);
  const { activationEmail } = useContext(UppAuthContext);
  const { toggleLanguageDropdownOpen, isLanguageDropdownOpen } =
    useContext(BillingMenuContext);
  const selectRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(selectRef, toggleLanguageDropdownOpen);

  const isOnActivationPage = location.pathname === routes.activation;

  useLayoutEffect(() => {
    const root = selectRef.current;
    if (!root) return;

    root
      .querySelector<HTMLElement>(".mdc-select__anchor")
      ?.setAttribute("aria-haspopup", "listbox");

    root
      .querySelector<HTMLElement>(".mdc-select__menu")
      ?.removeAttribute("role");

    const listbox = root.querySelector<HTMLElement>(
      ".mdc-select__menu .mdc-deprecated-list"
    );

    if (listbox) {
      listbox.setAttribute("role", "listbox");
      listbox.setAttribute("aria-labelledby", "language-select-label");
    }
  }, [isLanguageDropdownOpen]);

  const switchLanguage = (code: Language) => () => {
    if (isOnActivationPage) {
      updateUserLanguageNonAuth({ email: activationEmail, language: code });
    } else if (isAuthorized() && profile) {
      updateUser({
        language: code,
      });
    }
    changeLanguage(code);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      selectRef.current?.blur();
    }
  };

  const handleButtonClick = () => {
    toggleLanguageDropdownOpen();
  };

  const getItemLabel = (code: Language) => {
    return code === Language.EN
      ? intl.formatMessage(messages.languageSelectorMenuItemEnglishAriaLabel)
      : intl.formatMessage(messages.languageSelectorMenuItemSpanishAriaLabel);
  };

  const nextLanguage =
    currentLanguage === Language.EN ? Language.ES : Language.EN;

  return (
    <>
      <label id="language-select-label" className="visually-hidden">
        {intl.formatMessage(messages.languageSelectorMenuAriaLabel)}
      </label>
      <MaterialSelect
        onClick={handleButtonClick}
        ref={selectRef}
        labelId="language-select-label"
        className="custom-select"
        value={currentLanguage.toUpperCase()}
        placeholder={currentLanguage.toUpperCase()}
        onKeyDown={handleKeyDown}
        dropdownIcon={DownArrow}
        onValueChange={(value) => {
          if (value && value !== currentLanguage) {
            switchLanguage(value as Language)();
          }
        }}
        role="combobox"
        aria-labelledby="language-select-label"
      >
        <MaterialOption
          key={nextLanguage}
          value={nextLanguage}
          role="option"
          id={`language-option-${nextLanguage.toLowerCase()}`}
          aria-label={getItemLabel(nextLanguage)}
          aria-selected={nextLanguage === currentLanguage}
        >
          {nextLanguage.toUpperCase()}
        </MaterialOption>
      </MaterialSelect>
    </>
  );
};

export default LanguageSelector;
